import { DP_RULE_RESERVED_LABELS } from "./dp-rules.js";
import { ALERT_SEVERITIES } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/dp-rules.schemas.ts
/**
* Zod schemas of the DP-local alert rule set. The constants and the types are in `dp-rules.ts`.
*
* No schema here uses `.transform()` or `z.custom`: the set sits inside the release-engine
* desired-state DTO, and `createOpenApiDocument()` throws on either at boot. Refinements are
* allowed; `z.toJSONSchema` drops them without error.
*/
/** The most labels a rule may carry beside `severity` and `job`. */
const DP_RULE_MAX_EXTRA_LABELS = 14;
/** The most annotations a rule may carry. */
const DP_RULE_MAX_ANNOTATIONS = 16;
/** The longest `for` duration, in seconds: 24 hours. */
const DP_RULE_MAX_FOR_SECONDS = 86400;
/** The shortest group `interval`, in seconds. */
const DP_RULE_MIN_INTERVAL_SECONDS = 10;
/** The longest group `interval`, in seconds: 1 hour. */
const DP_RULE_MAX_INTERVAL_SECONDS = 3600;
const LABEL_NAME_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]{0,127}$/;
const ALERT_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]{0,127}$/;
const JOB_PATTERN = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
const FOR_PATTERN = /^(0|[1-9][0-9]{0,4})(s|m|h)$/;
const INTERVAL_PATTERN = /^[1-9][0-9]{0,3}(s|m|h)$/;
const GROUP_NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;
/**
* The seconds of a duration text that already matched `FOR_PATTERN` or `INTERVAL_PATTERN`.
* The last character is the unit (`s`, `m` or `h`); the rest is a decimal count.
*/
function durationSeconds(text) {
	const unit = text.slice(-1);
	return Number(text.slice(0, -1)) * (unit === "h" ? 3600 : unit === "m" ? 60 : 1);
}
const RESERVED_LABEL_SET = new Set(DP_RULE_RESERVED_LABELS);
const REQUIRED_LABELS = /* @__PURE__ */ new Set(["severity", "job"]);
/**
* The labels of one rule. `severity` and `job` are required, so each alert state names its
* severity and the platform service. Up to 14 more labels may follow.
*/
const DpRuleLabelsSchema = z.object({
	severity: z.enum(ALERT_SEVERITIES),
	job: z.string().regex(JOB_PATTERN)
}).catchall(z.string().max(256)).superRefine((labels, ctx) => {
	const extra = Object.keys(labels).filter((name) => !REQUIRED_LABELS.has(name));
	if (extra.length > DP_RULE_MAX_EXTRA_LABELS) ctx.addIssue({
		code: "custom",
		message: `a rule carries at most ${DP_RULE_MAX_EXTRA_LABELS} labels beside severity and job`
	});
	for (const name of extra) if (!LABEL_NAME_PATTERN.test(name) || name.startsWith("__") || RESERVED_LABEL_SET.has(name)) ctx.addIssue({
		code: "custom",
		path: [name],
		message: `label name ${JSON.stringify(name)} is malformed, starts with __ or is reserved`
	});
});
/**
* One alerting rule of the DP Mimir ruler.
*
* - `alert`: `^[A-Za-z_][A-Za-z0-9_]{0,127}$`. The ruler sets it as the `alertname` label, and
*   the operator program reports it as `dp_alertname` on `zion_dp_alert_active`.
* - `expr`: PromQL text, 1 to 8192 characters. The schema does not parse it. The DP ruler refuses a
*   bad expression at push, and the operator program reports that in `ObservabilityStatus.errors`.
* - `for`: `^(0|[1-9][0-9]{0,4})(s|m|h)$`, at most 24 h. `0s` fires at the first true evaluation.
* - `labels`: required `severity` (one of `ALERT_SEVERITIES` of `@zion/sdk-contracts`) and
*   required `job` (`^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$`, the platform service, for example
*   `core-api`). Up to 14 more labels: each name matches `^[a-zA-Z_][a-zA-Z0-9_]{0,127}$`, none
*   starts with `__`, none is in `DP_RULE_RESERVED_LABELS`, each value is at most 256 characters.
* - `annotations`: up to 16 entries, the same name grammar, each value at most 4096 characters.
*
* Errors: `safeParse` returns one zod issue per failed check, with its path, and never throws.
* Success returns the input values unchanged.
*/
const DpAlertRuleSchema = z.object({
	alert: z.string().regex(ALERT_NAME_PATTERN),
	expr: z.string().min(1).max(8192),
	for: z.string().regex(FOR_PATTERN, { abort: true }).refine((text) => durationSeconds(text) <= DP_RULE_MAX_FOR_SECONDS, { message: "for is at most 24h" }),
	labels: DpRuleLabelsSchema,
	annotations: z.record(z.string().regex(LABEL_NAME_PATTERN), z.string().max(4096)).refine((annotations) => Object.keys(annotations).length <= DP_RULE_MAX_ANNOTATIONS, { message: `a rule carries at most ${DP_RULE_MAX_ANNOTATIONS} annotations` })
}).strict();
/**
* One rule group of the DP Mimir ruler.
*
* - `name`: `^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$`, unique inside the set.
* - `interval`: `^[1-9][0-9]{0,3}(s|m|h)$`, from 10 s to 1 h.
* - `rules`: 1 to `DP_RULES_MAX_RULES_PER_GROUP` (100) rules.
*/
const DpRuleGroupSchema = z.object({
	name: z.string().regex(GROUP_NAME_PATTERN),
	interval: z.string().regex(INTERVAL_PATTERN, { abort: true }).refine((text) => {
		const seconds = durationSeconds(text);
		return seconds >= DP_RULE_MIN_INTERVAL_SECONDS && seconds <= DP_RULE_MAX_INTERVAL_SECONDS;
	}, { message: "interval is from 10s to 1h" }),
	rules: z.array(DpAlertRuleSchema).min(1).max(100)
}).strict();
/**
* The whole CP-managed rule set of one DP.
*
* `groups` holds 0 to `DP_RULES_MAX_GROUPS` (20) groups. Over the whole set, group names are
* unique (a duplicate fails at its `name` path) and the rule count is at most `DP_RULES_MAX_RULES`
* (200; the excess fails at path `groups`). An empty `groups` array is valid and means "the CP
* manages no rule for this DP".
*
* The set carries no DP id: the envelope is per DP already, and platform-api resolves the per-DP
* overrides before the set exists. Pure: parse has no side effect and returns the input unchanged.
*/
const DpRuleSetSchema = z.object({ groups: z.array(DpRuleGroupSchema).max(20) }).strict().superRefine((set, ctx) => {
	const seen = /* @__PURE__ */ new Set();
	let rules = 0;
	set.groups.forEach((group, index) => {
		if (seen.has(group.name)) ctx.addIssue({
			code: "custom",
			path: [
				"groups",
				index,
				"name"
			],
			message: `group name ${JSON.stringify(group.name)} appears twice`
		});
		seen.add(group.name);
		rules += group.rules.length;
	});
	if (rules > 200) ctx.addIssue({
		code: "custom",
		path: ["groups"],
		message: `a rule set holds at most 200 rules, got ${rules}`
	});
});
//#endregion
export { DpAlertRuleSchema, DpRuleGroupSchema, DpRuleSetSchema };

//# sourceMappingURL=dp-rules.schemas.js.map